/**
 * A stock's price as candles, with what a trader reads beside them: the price scale on the right,
 * volume under the candles, moving averages over them, an RSI panel below — and, drawn straight
 * across at their prices, the lines that are yours: where you stand (your average cost, with how many
 * shares and what they have made), where the stop rests, where the target waits. A level is a line,
 * not a point in time, so it is there whatever stretch of the chart you look at.
 *
 * The chart is walked, not paged: scroll to zoom around the pointer, drag to move through time.
 * The scale fits the candles in view; a level too far away to fit is named at the edge it lies beyond,
 * never allowed to squash the candles.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Minus, Plus } from 'lucide-react'
import { cx } from './kit'
import type { Bars } from './bars'

export interface PriceLine {
  y: number
  /** yours (the position), a gain to take, a loss to stop */
  tone: 'pos' | 'up' | 'down'
  /** What the line says, always. */
  chip: ReactNode
  /** What it adds when the pointer is on it. */
  more?: ReactNode
}
const TONE = { pos: '#e8e8e8', up: 'var(--color-ws-up)', down: 'var(--color-ws-down)' }
const AVG = ['#d0d0d0', '#8a8a8a', '#5a5a5a']
const vol = (v: number) => (v >= 1e9 ? `${(v / 1e9).toFixed(2)}B` : v >= 1e6 ? `${(v / 1e6).toFixed(2)}M` : `${(v / 1e3).toFixed(1)}K`)
/** A round step for a scale: 1, 2 or 5 times a power of ten. */
const nice = (span: number, parts: number) => {
  const raw = span / parts
  const mag = 10 ** Math.floor(Math.log10(raw))
  return ([1, 2, 5, 10].find((m) => m * mag >= raw) ?? 10) * mag
}
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v))
const VIEW = 96 // candles on screen to begin with
const FEW = 16 // the closest one may zoom in

export function Candles({ bars, height = 440, lines = [], band, overlays = [], volume = true, rsi = true, onScrub }: { bars: Bars; height?: number; lines?: PriceLine[]; band?: [number, number]; overlays?: { points: number[]; label: string }[]; volume?: boolean; rsi?: boolean; onScrub?: (i: number | null) => void }) {
  const box = useRef<HTMLDivElement>(null)
  const total = bars.c.length
  const [w, setW] = useState(0)
  const [view, setView] = useState({ count: Math.min(total, VIEW), end: total })
  const [at, setAtRaw] = useState<number | null>(null) // (an index into the whole row of candles)
  const [py, setPy] = useState<number | null>(null)
  const drag = useRef<{ x: number; end: number } | null>(null)
  const setAt = (i: number | null) => (setAtRaw(i), onScrub?.(i))
  useEffect(() => {
    const el = box.current
    if (!el) return
    const watch = new ResizeObserver(() => setW(el.clientWidth))
    watch.observe(el)
    return () => watch.disconnect()
  }, [])
  // other candles (another interval, another stock): start again from the newest
  useEffect(() => setView({ count: Math.min(total, VIEW), end: total }), [bars, total])

  const AXIS = 62
  const FOOT = 22
  const RSI = rsi ? 92 : 0
  const GAP = rsi ? 14 : 0
  const H = height - FOOT - RSI - GAP // the price panel
  const VOL = volume ? Math.round(H * 0.2) : 0
  const pw = Math.max(0, w - AXIS)
  const count = clamp(view.count, Math.min(FEW, total), total)
  const end = clamp(view.end, count, total)
  const start = end - count
  const step = count ? pw / count : 0
  const bw = Math.max(1, Math.min(15, Math.floor(step * 0.66)))
  const x = (j: number) => step * (j + 0.5)

  /** Zoom by a factor, keeping the candle at `frac` of the width where it is. */
  const zoom = (f: number, frac = 1) =>
    setView((v) => {
      const c0 = clamp(v.count, Math.min(FEW, total), total)
      const e0 = clamp(v.end, c0, total)
      const c1 = clamp(Math.round(c0 * f), Math.min(FEW, total), total)
      const anchor = e0 - c0 + frac * c0
      return { count: c1, end: clamp(Math.round(anchor - frac * c1) + c1, c1, total) }
    })
  // the wheel belongs to the chart while the pointer is on it: up and down zoom, sideways walks
  useEffect(() => {
    const el = box.current
    if (!el) return
    const wheel = (e: WheelEvent) => {
      e.preventDefault()
      const r = el.getBoundingClientRect()
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) setView((v) => ({ ...v, end: clamp(v.end + Math.round(e.deltaX / 12), v.count, total) }))
      else zoom(e.deltaY > 0 ? 1.14 : 1 / 1.14, clamp((e.clientX - r.left) / Math.max(1, r.width - AXIS), 0, 1))
    }
    el.addEventListener('wheel', wheel, { passive: false })
    return () => el.removeEventListener('wheel', wheel)
  }, [total]) // eslint-disable-line react-hooks/exhaustive-deps

  // what is in view
  const idx = Array.from({ length: count }, (_, j) => start + j)
  const hiC = Math.max(...idx.map((i) => bars.h[i]))
  const loC = Math.min(...idx.map((i) => bars.l[i]))
  const reach = (hiC - loC || 1) * 0.6
  const near = lines.filter((l) => l.y >= loC - reach && l.y <= hiC + reach)
  const far = lines.filter((l) => !near.includes(l))
  const all = [hiC, loC, ...near.map((l) => l.y), ...(band ?? []).filter((b) => b >= loC - reach && b <= hiC + reach), ...overlays.flatMap((o) => idx.map((i) => o.points[i]))]
  const min = Math.min(...all)
  const max = Math.max(...all)
  const pad = (max - min || 1) * 0.08
  const lo = min - pad
  const hi = max + pad
  const TOPPAD = 28 // room for the readout
  const floor = H - VOL - 6
  const y = (v: number) => TOPPAD + (1 - (v - lo) / (hi - lo)) * (floor - TOPPAD)
  const unY = (p: number) => lo + (1 - (p - TOPPAD) / (floor - TOPPAD)) * (hi - lo)
  const tick = nice(hi - lo, 6)
  const ticks: number[] = []
  for (let t = Math.ceil(lo / tick) * tick; t < hi; t += tick) ticks.push(t)
  const digits = tick < 1 ? 2 : tick < 10 ? 1 : 0
  const last = bars.c[total - 1]
  const up = (i: number) => bars.c[i] >= bars.o[i]
  const ink = (i: number) => (up(i) ? 'var(--color-ws-up)' : 'var(--color-ws-down)')
  const vmax = Math.max(...idx.map((i) => bars.v[i]))
  const rTop = H + GAP
  const ry = (v: number) => rTop + 6 + (1 - v / 100) * (RSI - 12)
  const read = at ?? total - 1
  const dates = count > 4 ? [0, 1, 2, 3, 4].map((k) => Math.round(((count - 1) * k) / 4)) : []
  const lastIn = last >= lo && last <= hi

  const move = (e: React.PointerEvent) => {
    const r = e.currentTarget.getBoundingClientRect()
    const px = e.clientX - r.left
    const yy = e.clientY - r.top
    if (drag.current && step) {
      const by = Math.round((drag.current.x - e.clientX) / step)
      setView((v) => ({ ...v, end: clamp(drag.current!.end + by, v.count, total) }))
    }
    if (px > pw || !step) return (setAt(null), setPy(null))
    setAt(start + clamp(Math.floor(px / step), 0, count - 1))
    setPy(yy < H - VOL ? yy : null)
  }
  // The prices that stand on the scale in a filled tag: your levels and the last price. Two that are
  // close would cover each other, so each is pushed just clear of the one above it.
  const tags = [...near.map((l) => ({ v: l.y, fill: l.tone === 'pos' ? '#2e2e2e' : TONE[l.tone], color: l.tone === 'pos' ? '#fff' : '#000' })), ...(lastIn ? [{ v: last, fill: ink(total - 1), color: '#000' }] : [])]
    .map((t) => ({ ...t, at: y(t.v) }))
    .sort((a, b) => a.at - b.at)
  tags.forEach((t, k) => {
    if (k && t.at - tags[k - 1].at < 19) t.at = tags[k - 1].at + 19
  })
  // …and the labels of your lines stand side by side when their lines are close.
  const chips = near.map((l) => ({ l, at: y(l.y), col: 0 })).sort((a, b) => a.at - b.at)
  chips.forEach((c, k) => {
    if (k && c.at - chips[k - 1].at < 26) c.col = chips[k - 1].col + 1
  })
  const small = 'pointer-events-auto flex h-6 items-center justify-center rounded-[6px] border border-ws-edge bg-ws-bg text-ws-fg-3 transition-colors duration-150 hover:text-ws-fg'
  return (
    <div
      ref={box}
      className={cx('relative w-full touch-none select-none', drag.current ? 'cursor-grabbing' : 'cursor-crosshair')}
      style={{ height }}
      onPointerMove={move}
      onPointerDown={(e) => {
        if ((e.target as Element).closest('button')) return
        drag.current = { x: e.clientX, end }
        e.currentTarget.setPointerCapture(e.pointerId)
        move(e)
      }}
      onPointerUp={() => (drag.current = null)}
      onPointerCancel={() => (drag.current = null)}
      onPointerLeave={() => (setAt(null), setPy(null))}
      onDoubleClick={() => setView({ count: Math.min(total, VIEW), end: total })}
    >
      {w > 0 && count > 0 && (
        <svg width={w} height={height} className="block" aria-hidden>
          {/* the scale */}
          {ticks.map((t) => (
            <g key={t}>
              <line x1={0} x2={pw} y1={Math.round(y(t)) + 0.5} y2={Math.round(y(t)) + 0.5} stroke="var(--color-ws-hair)" />
              <text x={w - 2} y={y(t) + 4} textAnchor="end" fontSize="11" fill="#7a7a7a" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {t.toFixed(digits)}
              </text>
            </g>
          ))}
          <line x1={pw + 0.5} x2={pw + 0.5} y1={0} y2={height - FOOT} stroke="var(--color-ws-line)" />
          {band && band[1] > lo && band[0] < hi && <rect x={0} width={pw} y={y(Math.min(band[1], hi))} height={Math.max(0, y(Math.max(band[0], lo)) - y(Math.min(band[1], hi)))} fill="var(--color-ws-up)" opacity="0.1" />}

          {/* volume, then the averages, then the candles over both */}
          {volume && idx.map((i, j) => <rect key={i} x={x(j) - bw / 2} y={H - Math.max(1, (bars.v[i] / vmax) * (VOL - 6))} width={bw} height={Math.max(1, (bars.v[i] / vmax) * (VOL - 6))} fill={ink(i)} opacity={at === i ? 0.6 : 0.3} />)}
          {overlays.map((o, k) => (
            <path key={o.label} d={idx.map((i, j) => `${j ? 'L' : 'M'}${x(j).toFixed(1)} ${y(o.points[i]).toFixed(1)}`).join('')} fill="none" stroke={AVG[k % 3]} strokeWidth="1.25" strokeLinejoin="round" />
          ))}
          {idx.map((i, j) => (
            <g key={i} stroke={ink(i)} fill={ink(i)}>
              <line x1={Math.round(x(j)) + 0.5} x2={Math.round(x(j)) + 0.5} y1={y(bars.h[i])} y2={y(bars.l[i])} strokeWidth="1" />
              <rect x={x(j) - bw / 2} y={y(Math.max(bars.c[i], bars.o[i]))} width={bw} height={Math.max(1, Math.abs(y(bars.c[i]) - y(bars.o[i])))} stroke="none" />
            </g>
          ))}

          {/* the last price, and your own levels straight across */}
          {lastIn && <line x1={0} x2={pw} y1={Math.round(y(last)) + 0.5} y2={Math.round(y(last)) + 0.5} stroke={ink(total - 1)} strokeDasharray="1 4" strokeLinecap="round" />}
          {near.map((l, k) => (
            <line key={k} x1={0} x2={pw} y1={Math.round(y(l.y)) + 0.5} y2={Math.round(y(l.y)) + 0.5} stroke={TONE[l.tone]} strokeWidth="1" strokeDasharray={l.tone === 'pos' ? undefined : '5 4'} opacity={l.tone === 'pos' ? 0.6 : 0.95} />
          ))}
          {tags.map((t, k) => (
            <g key={k}>
              <rect x={pw + 3} y={t.at - 9} width={AXIS - 3} height={18} rx={4} fill={t.fill} />
              <text x={pw + 3 + (AXIS - 3) / 2} y={t.at + 4} textAnchor="middle" fontSize="11" fontWeight="600" fill={t.color} style={{ fontVariantNumeric: 'tabular-nums' }}>
                {t.v.toFixed(2)}
              </text>
            </g>
          ))}

          {/* RSI */}
          {rsi && (
            <>
              <line x1={0} x2={w} y1={rTop - GAP / 2 + 0.5} y2={rTop - GAP / 2 + 0.5} stroke="var(--color-ws-line)" />
              <rect x={0} width={pw} y={ry(70)} height={ry(30) - ry(70)} fill="#fff" opacity="0.03" />
              {[70, 30].map((v) => (
                <g key={v}>
                  <line x1={0} x2={pw} y1={Math.round(ry(v)) + 0.5} y2={Math.round(ry(v)) + 0.5} stroke="#3a3a3a" strokeDasharray="3 4" />
                  <text x={w - 2} y={ry(v) + 4} textAnchor="end" fontSize="11" fill="#7a7a7a">
                    {v}
                  </text>
                </g>
              ))}
              <path d={idx.map((i, j) => `${j ? 'L' : 'M'}${x(j).toFixed(1)} ${ry(bars.rsi[i]).toFixed(1)}`).join('')} fill="none" stroke="#d0d0d0" strokeWidth="1.25" strokeLinejoin="round" />
            </>
          )}

          {/* the dates */}
          {dates.map((d, k) => (
            <text key={k} x={clamp(x(d), 0, pw)} y={height - 6} textAnchor={k === 0 ? 'start' : k === 4 ? 'end' : 'middle'} fontSize="11" fill="#7a7a7a">
              {bars.labels[start + d]}
            </text>
          ))}

          {/* the cross under the pointer */}
          {at != null && at >= start && at < end && <line x1={Math.round(x(at - start)) + 0.5} x2={Math.round(x(at - start)) + 0.5} y1={0} y2={height - FOOT} stroke="#4a4a4a" />}
          {py != null && (
            <>
              <line x1={0} x2={pw} y1={Math.round(py) + 0.5} y2={Math.round(py) + 0.5} stroke="#4a4a4a" />
              <rect x={pw + 3} y={py - 9} width={AXIS - 3} height={18} rx={4} fill="#3a3a3a" />
              <text x={pw + 3 + (AXIS - 3) / 2} y={py + 4} textAnchor="middle" fontSize="11" fill="#fff" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {unY(py).toFixed(2)}
              </text>
            </>
          )}
        </svg>
      )}

      {/* the candle read out in words: the one under the pointer, or the last */}
      {total > 0 && (
        <div className="pointer-events-none absolute left-0 top-0 flex h-5 items-center gap-3 overflow-hidden whitespace-nowrap text-[12px] leading-5 tracking-normal tabular-nums" style={{ maxWidth: Math.max(0, pw - 96) }}>
          <span className="text-ws-fg-2">{bars.labels[read]}</span>
          {(
            [
              ['O', bars.o[read]],
              ['H', bars.h[read]],
              ['L', bars.l[read]],
              ['C', bars.c[read]],
            ] as const
          ).map(([k, v]) => (
            <span key={k} className="text-ws-fg-3">
              {k} <span className={up(read) ? 'text-ws-up' : 'text-ws-down'}>{v.toFixed(2)}</span>
            </span>
          ))}
          {volume && (
            <span className="text-ws-fg-3">
              V <span className="text-ws-fg-2">{vol(bars.v[read])}</span>
            </span>
          )}
          {overlays.map((o, k) => (
            <span key={o.label} style={{ color: AVG[k % 3] }}>
              {o.label} {o.points[read].toFixed(2)}
            </span>
          ))}
        </div>
      )}
      {rsi && w > 0 && (
        <div className="pointer-events-none absolute left-0 flex h-4 items-center gap-2 text-[12px] leading-4 tracking-normal tabular-nums text-ws-fg-3" style={{ top: rTop - 1 }}>
          RSI 14 <span className="text-ws-fg-2">{bars.rsi[read].toFixed(2)}</span>
        </div>
      )}

      {/* closer, further, back to the newest */}
      {w > 0 && (
        <div className="pointer-events-none absolute top-0 flex gap-1" style={{ right: AXIS + 8 }}>
          <button type="button" aria-label="Zoom out" title="Zoom out" onClick={() => zoom(1.5)} className={cx(small, 'w-6')}>
            <Minus size={13} strokeWidth={2} />
          </button>
          <button type="button" aria-label="Zoom in" title="Zoom in" onClick={() => zoom(1 / 1.5)} className={cx(small, 'w-6')}>
            <Plus size={13} strokeWidth={2} />
          </button>
          {(end < total || count !== Math.min(total, VIEW)) && (
            <button type="button" onClick={() => setView({ count: Math.min(total, VIEW), end: total })} className={cx(small, 'px-2 text-[12px] tracking-normal')}>
              Latest
            </button>
          )}
        </div>
      )}

      {/* what each of your lines says; more when the pointer is on it */}
      {w > 0 &&
        chips.map(({ l, at: top, col }, k) => (
          <div key={k} className="group absolute z-10 flex h-[22px] -translate-y-1/2 cursor-default items-center gap-2 whitespace-nowrap rounded-[6px] border bg-ws-bg px-2 text-[12px] leading-none tracking-normal tabular-nums text-ws-fg-2 transition-colors duration-150 hover:z-20 hover:text-ws-fg" style={{ top, left: 6 + col * 132, borderColor: l.tone === 'pos' ? '#5a5a5a' : TONE[l.tone] }}>
            {l.chip}
            {l.more && <span className="hidden text-ws-fg-3 group-hover:inline">{l.more}</span>}
          </div>
        ))}
      {/* a level beyond the candles in view: named at the edge it lies past */}
      {w > 0 &&
        (['above', 'below'] as const).map((side) => {
          const list = far.filter((l) => (side === 'above' ? l.y > hi : l.y < lo))
          if (!list.length) return null
          return (
            <div key={side} className="absolute z-10 flex gap-2" style={{ left: 0, top: side === 'above' ? TOPPAD + 2 : floor - 24 }}>
              {list.map((l, k) => (
                <div key={k} className="group flex h-[22px] cursor-default items-center gap-2 whitespace-nowrap rounded-[6px] border bg-ws-bg px-2 text-[12px] leading-none tracking-normal tabular-nums text-ws-fg-2" style={{ borderColor: l.tone === 'pos' ? '#5a5a5a' : TONE[l.tone] }}>
                  <span className="text-ws-fg-3">{side === 'above' ? '↑' : '↓'}</span>
                  {l.chip}
                  <span className="text-ws-fg-3">{l.y.toFixed(2)}</span>
                  {l.more && <span className="hidden text-ws-fg-3 group-hover:inline">{l.more}</span>}
                </div>
              ))}
            </div>
          )
        })}
    </div>
  )
}
